import React, { useState } from 'react';
import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// 30-min slots from 7:00 AM to 9:00 PM
const TIME_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  for (let m = 0; m < 60; m += 30) {
    if (h === 21 && m > 0) break;
    const hour12 = h % 12 || 12;
    const ampm = h < 12 ? 'AM' : 'PM';
    TIME_SLOTS.push({
      label: `${hour12}:${String(m).padStart(2, '0')} ${ampm}`,
      value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
    });
  }
}

const parseValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const DateTimePicker = ({ value, onChange, placeholder, className, hasError }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selectedDate = parseValue(value);
  const selectedTime = selectedDate
    ? `${String(selectedDate.getHours()).padStart(2, '0')}:${String(selectedDate.getMinutes()).padStart(2, '0')}`
    : '';

  const buildISO = (date, timeStr) => {
    const [h, m] = timeStr ? timeStr.split(':').map(Number) : [8, 0];
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    onChange(buildISO(date, selectedTime || '08:00'));
  };

  const handleTimeSelect = (timeStr) => {
    const base = selectedDate || new Date();
    onChange(buildISO(base, timeStr));
    if (selectedDate) setOpen(false);
  };

  const display = selectedDate
    ? format(selectedDate, "MMM d, yyyy 'at' h:mm a")
    : placeholder || t('datetime.no_date');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={cn(
            'w-full justify-start text-left font-normal h-12',
            !selectedDate && 'text-muted-foreground',
            hasError && 'border-red-500 focus-visible:ring-red-500',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {display}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="center">
        <div className="flex divide-x">
          {/* Date picker */}
          <div>
            <Calendar
              mode="single"
              selected={selectedDate ?? undefined}
              onSelect={handleDateSelect}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </div>
          {/* Time picker — 3-column grid, same visual weight as calendar */}
          <div className="p-3 flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t('datetime.select_time')}
            </p>
            <div className="grid grid-cols-3 gap-1">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() => handleTimeSelect(slot.value)}
                  className={cn(
                    'text-xs px-1.5 py-1.5 rounded-md text-center transition-colors whitespace-nowrap',
                    selectedTime === slot.value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { DateTimePicker };
